import { useState, useMemo } from "react";
import Link from "next/link";
import Map from "../Map/Map";
import ScrollNav from "components/ui/nav/ScrollNav/ScrollNav";
import DestinationLinkCard from "components/card/DestinationLinkCard";
import SectionTitle from "components/typography/SectionTitle/SectionTitle";
import { BiChevronLeft } from "react-icons/bi";
import { FaList, FaMapMarkedAlt } from "react-icons/fa";
import { regions } from "lib/data";

import useWindowSize from "lib/hooks/useWindowSize";

import styles from "./MapLayout.module.scss";

export default function MapLayout({ data, geoJSON }) {
  const { width } = useWindowSize();

  const [latLng, setLatLng] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(4);
  const [isSidebarActive, setIsSidebarActive] = useState(false);

  const [currentRegionId, setCurrentRegionId] = useState(regions[0].region);

  // useEffect(() => {
  //   width > 992 && !isSidebarActive
  //     ? setIsSidebarActive(true)
  //     : setIsSidebarActive(false);
  // }, [width]);

  const { destinations } = data;
  const navTags = regions.map((region) => {
    return {
      text: region.regionName,
      tag: region.region,
      active: true,
    };
  });

  let destinationsByRegion = useMemo(() => {
    let regionsObject = {};

    const sortedDestinations = [...destinations]?.sort((a, b) => {
      if (a.destination_title > b.destination_title) {
        return 1;
      } else if (b.destination_title > a.destination_title) {
        return -1;
      }
      return 0;
    });

    for (let destination of sortedDestinations) {
      if (!destination) {
        break;
      }

      const destinationRegion =
        destination?.destination_details?.region?.region;

      if (!regionsObject[destinationRegion]) {
        regionsObject[destinationRegion] = {
          region: destination?.destination_details?.region,
          destinations: [],
        };
      }

      const destinationObject = {
        ...destination,
        linkCard: (
          <DestinationLinkCard
            destination={destination}
            locationAsTag={true}
            key={destination.id}
            cardProps={{
              divider: false,
              titleHeight: 0,
              loading:
                regionsObject[destinationRegion].destinations.length <=
                (width <= 380 ? 1 : 3)
                  ? "eager"
                  : "lazy",
              headerFontSize: "var(--font-size-md",
              sizes: `
            (min-width: 1240px) 380px,
            (min-width: 992px) 25vw,
            (min-width: 550px) 380px,
            (min-width: 390px) 40vw,
            (max-width: 390px) 100vw, 
            380px`,
            }}
          />
        ),
      };

      regionsObject[destinationRegion].destinations.push(destinationObject);
    }
    return regionsObject;
  }, [destinations]);

  const regionSections = useMemo(
    () =>
      Object.values(destinationsByRegion)
        // ?.slice(0, 1)
        ?.sort((a, b) => {
          if (a.region.region > b.region.region) {
            return 1;
          } else if (b.region.region > a.region.region) {
            return -1;
          }
          return 0;
        })
        ?.map((region, index) => {
          const regionSlug = region.region.region;
          const regionName = region.region.regionName;

          return {
            regionId: regionSlug,
            element: (
              <div
                className={`${styles.region}`}
                key={`${regionSlug}-${index}`}
              >
                <h2 id={regionSlug} className={`${styles.regionHeader}`}>
                  <Link
                    href={`/destinations/${regionSlug}`}
                    className={`${styles.regionLink}`}
                  >
                    {regionName}
                  </Link>
                </h2>
                <div className={`${styles.cards}`}>
                  {region?.destinations?.map(
                    (destination) => destination?.linkCard
                  )}
                </div>
              </div>
            ),
          };
        }),
    [destinationsByRegion]
  );

  const handleScrollNavClick = (event, tag) => {
    window.scrollTo(0, 0);

    setCurrentRegionId(tag.tag);

    if (isSidebarActive && width < 992) {
      return;
    }

    if (!isSidebarActive) {
      event.preventDefault();
    }

    const clickedRegion = regions.find((region) => region.region === tag.tag);
    setLatLng({
      lat: clickedRegion?.latLng?.lat,
      lng: clickedRegion?.latLng?.lng,
    });
    setZoom(clickedRegion?.zoom);
  };

  const handleSidebarToggle = (event) => {
    window.scrollTo(0, 0);

    setIsSidebarActive((previous) => {
      return !previous;
    });
  };

  return (
    <>
      <ScrollNav
        anchorTags={navTags}
        justify={width > 992 ? "center" : "start"}
        style="miniButtons"
        containerStyles={{ padding: "var(--spacing-sm) var(--spacing-sm)" }}
        onClick={handleScrollNavClick}
      />
      <div className={`${styles.layout}`}>
        <main
          className={`${styles.sidebarMap}  ${
            isSidebarActive ? styles.showSidebar : styles.hideSidebar
          }`}
        >
          <aside
            className={`${styles.sidebar} ${
              isSidebarActive ? styles.activeSidebar : styles.hiddenSidebar
            }`}
          >
            <h1 className={`${styles.destinationsHeading}`}>
              <SectionTitle title="Explore Travel Destinations" />
            </h1>
            <div className={`${styles.regionSections}`}>
              {regionSections &&
                isSidebarActive &&
                regionSections.find(
                  (regionSection) => regionSection.regionId === currentRegionId
                ).element}
            </div>
          </aside>
          <section className={`${styles.map}`}>
            {width > 992 && (
              <button
                className={`${styles.sidebarToggle} ${styles.arrowButton} ${
                  isSidebarActive ? "" : styles.clicked
                }`}
                onClick={handleSidebarToggle}
                name="toggle-map-sidebar"
                aria-label="Toggle map sidebar"
              >
                <BiChevronLeft size="30" />
              </button>
            )}
            <Map
              data={data}
              geoJSON={geoJSON}
              latLng={{ lat: latLng?.lat ?? 0, lng: latLng?.lng ?? 0 }}
              zoom={zoom}
              resize={isSidebarActive}
            ></Map>
          </section>
          {width < 992 && (
            <span className={`${styles.sidebarToggleContainer}`}>
              <button
                className={`${styles.sidebarToggle} ${styles.showMapButton} ${
                  isSidebarActive ? "" : styles.clicked
                }`}
                onClick={handleSidebarToggle}
                name="toggle-map-overlay"
                aria-label="Toggle map overlay"
              >
                {isSidebarActive ? (
                  <span className={`${styles.buttonContent}`}>
                    <FaMapMarkedAlt size="25" color="inherit"></FaMapMarkedAlt>
                    <span className={`${styles.buttonText}`}>Map</span>
                  </span>
                ) : (
                  <span className={`${styles.buttonContent}`}>
                    <FaList size="25" color="inherit"></FaList>
                    <span className={`${styles.buttonText}`}>List</span>
                  </span>
                )}
              </button>
            </span>
          )}
        </main>
      </div>
    </>
  );
}
